// ── The seed state: a canvas that already has a reading on it ──────
//
// Every value here was MEASURED, not chosen. Ten candidates went to the real
// model three times through the real proxy, the six that behaved stably were
// kept, and those six were measured three times again — because a reading is
// made from the whole set at once, so stability at ten is not stability at six.
// It was not: `wet swimsuit === sunburn` was strong 3/3 among ten candidates
// and weak 3/3 among the six. See docs/seed-selection.md for all six runs.
//
// The reading below is run 1 of the six-fragment round, verbatim. Two of the
// three runs produced exactly this structure; taking the first observation is
// the least selective rule available, and the run that differed is recorded
// alongside it rather than discarded.
//
// It is baked rather than fetched so the first screen cannot land on a network
// failure, and because a seeded canvas whose reading arrived by API call would
// be a different reading on a different day.

// The prepared example was generated under v6.0.1-dev and is reported as such.
// v6.0.2 changed nothing that shapes a reading, but a canned reading has to say
// which protocol actually produced it — restamping it would be the one place in
// the instrument where a version is a label rather than a record.
export const SEED_PROTOCOL_VERSION = "v6.0.1-dev";
export const SEED_MODEL_VERSION = "claude-opus-4-5-20251101";
export const SEED_LENS = "PATTERN";
export const SEED_LANGUAGE = "en";

// Positions are NORMALISED (3-1). The canvas they are placed on is whatever
// window the visitor has, so the seed carries a relative layout and the pixels
// are derived at mount — the same rule the frozen record follows.
//
// The layout is doing a job. The two fragments the model refused every time sit
// closest together, so the first screen has a real unread adjacency; the pair
// it called strong every time sits in opposite corners, so it has a real
// model-surprise. Verified to hold on every window shape in
// scripts/verify-seed.mjs — a layout that only works at one aspect ratio would
// be a demonstration of the window, not of the instrument.
// The strong pair leads on BOTH axes — it has the largest horizontal gap and
// the largest vertical gap of any pair here. Since distance is
// (w·dx)² + (h·dy)², a pair that leads on both leads for every positive window,
// so "the furthest apart the arrangement allows" is true at 3:1 and at 1:1.
// An earlier layout led only on dy and dropped to 4th-of-5 at 2560x700.
export const SEED_FRAGMENTS = [
  { id: "nszwvq", text: "cold tiles",   nx: 0.12, ny: 0.82 },
  { id: "3aj8ns", text: "wet swimsuit", nx: 0.88, ny: 0.18 },
  { id: "by9zvo", text: "sunburn",      nx: 0.80, ny: 0.78 },
  { id: "4vkv26", text: "tapas",        nx: 0.55, ny: 0.52 },
  // refused in all three runs, and never named in any relation
  { id: "aho9na", text: "Joan Miró",    nx: 0.29, ny: 0.30 },
  { id: "qbtx5t", text: "Casa Milà",    nx: 0.22, ny: 0.22 },
];

export const SEED_READING = {
  relations: [
    { a: "3aj8ns", b: "nszwvq", fit: "strong", label: "cold wet skin shock" },
    { a: "by9zvo", b: "nszwvq", fit: "strong", label: "hot skin meets cold" },
    { a: "3aj8ns", b: "by9zvo", fit: "weak", label: "skin tight and sore" },
  ],
  unlinked: ["4vkv26", "aho9na", "qbtx5t"],
  fragments: [
    { id: "3aj8ns", words: ["cold", "sticky", "heavy", "tight"] },
    { id: "4vkv26", words: ["salty", "oily", "small", "warm"] },
    { id: "aho9na", words: ["bright", "round", "sharp"] },
    { id: "by9zvo", words: ["hot", "tight", "stinging", "red"] },
    { id: "nszwvq", words: ["hard", "smooth", "cold", "bare"] },
    { id: "qbtx5t", words: ["wavy", "heavy", "bumpy", "tall"] },
  ],
};

// Turn the normalised seed into fragments on this particular canvas. The app's
// fragment shape, so nothing downstream can tell a seeded fragment from one the
// visitor placed — except the provenance below.
//
// `origin` is NOT the UI's "you are looking at a prepared example" flag. That
// one is claimed away the moment the visitor touches anything, which is right
// for a caption and wrong for a record: session 01 could only be read back
// because the seed ids happen to be literals. Provenance is decided here and
// never written again.
export function seedFragments(field) {
  return SEED_FRAGMENTS.map(f => ({
    id: f.id, text: f.text,
    // nx/ny is the position; x/y is where that lands on this canvas.
    nx: f.nx, ny: f.ny,
    x: f.nx * field.w, y: f.ny * field.h,
    type: "text", named: true, moves: 0,
    origin: "seed",
    // The seed's own normalised coordinates, not a re-derivation from the
    // pixels above. Those pixels are a projection into whichever window this
    // visitor has, and this effect re-runs on every resize.
    initialPosition: { nx: f.nx, ny: f.ny },
    // A seeded fragment has no creation moment in this session: it was already
    // there when the session opened. Stamping one would repeat the mistake the
    // prepared reading's clock made — a time that reads as something that
    // happened when nothing did.
    createdAt: null,
  }));
}
